import { FC, lazy, Suspense } from 'react';
import { useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { CarouselItem } from '@api/types/carousel';
import { City as CityType } from '@api/types/cities';
import { Analytics, AnalyticsPages } from '@components/analytics/analytics';
import SwiperMobileSlider from '@components/carousel/SwiperMobileSlider';
import SwiperSlider from '@components/carousel/SwiperSlider';
import WhatsappLink from '@components/common/WhatsappLink';
import Footer from '@components/landing/footer';
import Loading from '@components/layout/Loading';
import { tw } from '@utils/tailwind-utils';
import { showErrorToast } from '@utils/toast';
import { getWhatsAppCountry } from '@utils/whatsappNumber';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

interface LandingIndexProps {
  cities: CityType[];
  locale: string | undefined;
  imagesCarousel: CarouselItem[];
}

const Blog = lazy(() => import('@components/landing/blog/Blog'));
const City = lazy(() => import('@components/landing/city'));
const FAQ = lazy(() => import('@components/landing/faq'));
const News = lazy(() => import('@components/landing/news'));

const LandingIndex: FC<LandingIndexProps> = ({ cities, imagesCarousel }) => {
  const { t } = useTranslation(['landing', 'common', 'countries']);
  const noCityData = !cities.length;

  useEffect(() => {
    if (noCityData) {
      showErrorToast({ message: t('common:error.general-error') });
    }
  }, [noCityData, t]);

  useEffect(() => {
    Analytics.event(AnalyticsPages.WEB_HOME, { isMobile });
  }, []);

  const renderItem = (item: CarouselItem) => {
    const content = (
      <div className="w-full max-w-[900px] overflow-hidden rounded-3xl min-[250px]:h-[120px] min-[350px]:h-[180px] min-[500px]:h-[220px] min-[800px]:h-[250px] min-[1000px]:h-[300px] lg:h-[350px]">
        <Image
          unoptimized
          key={item.alt}
          width={900}
          height={350}
          alt={item.alt}
          src={item.url}
          className="h-full w-full object-center"
        />
      </div>
    );

    return item.redirectLink ? (
      <a href={item.redirectLink} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
        {content}
      </a>
    ) : (
      <div>{content}</div>
    );
  };

  return (
    <>
      {imagesCarousel && imagesCarousel.length > 0 && (
        <section className="mx-auto w-[100vw] max-w-[2750px] pb-10 pt-44 max-[600px]:pt-72 tablet:pt-52 tablet:text-[34px] lg:pb-20 lg:pt-28">
          <div className="hidden min-[700px]:block">
            <SwiperSlider items={imagesCarousel} renderItem={renderItem} spaceBetween={35} />
          </div>
          <div className="block px-4 min-[700px]:hidden">
            <SwiperMobileSlider items={imagesCarousel} renderItem={renderItem} />
          </div>
        </section>
      )}
      <Suspense fallback={<Loading />}>
        <section
          className={tw('flex w-full flex-col items-center justify-center bg-[#F2F2F2] pb-10 tablet:pb-[280px]', {
            'mt-0': noCityData
          })}
        >
          {!noCityData && <City cities={cities} />}
          <Blog
            className={tw({
              'pt-10': noCityData
            })}
          />
        </section>
        <section id="faq-section" className="my-10 flex w-full items-center justify-center lg:mt-72">
          <FAQ />
        </section>
        <section className="flex w-full items-center justify-center bg-gray-100">
          <News />
        </section>
      </Suspense>
      <footer className="w-full">
        <Footer cities={cities} showReview />
      </footer>
      <aside>
        <WhatsappLink whatsappCountry={getWhatsAppCountry(cities[0].country)} classNames="z-[100]" />
      </aside>
    </>
  );
};

export default LandingIndex;
